import { defineStyleConfig, extendTheme, theme as baseTheme } from '@chakra-ui/react';
import { BaseThemeTypings } from '@chakra-ui/styled-system';
import { getTenantByHost } from '@pelicargo/tenants';
import { RequestStatus } from '@pelicargo/types';
import { ShipmentStatus } from '@prisma/client';

import { Button, CustomRadio, SimpleField, SimpleTooltip, Table } from './components';
import card from './components/card';
import formError from './components/form-error';
import Heading from './components/heading';
import labeledValue from './components/labeled-value';
import newsBanner from './components/news-banner';
import RadioCard from './components/radio-card';
import tag from './components/tag';
import * as foundations from './foundations';
import { themeVar } from './tenantTheme';

/**
 * Per-tenant theming: how the CSS-variable layer is wired, and what cannot go through it.
 *
 * `ChakraProvider` is mounted above `<TRPC>` in `src/main.tsx` and this theme object is built at
 * module-eval time, so a tRPC-fetched value can never reach the theme itself. Instead every
 * overridable token *value* is `var(--peli-<path>, <current static value>)` (see
 * `modelUtils.customerTheme.cssVar`). Chakra re-emits each token as its own custom property, so
 * `--chakra-colors-primary-500: var(--peli-colors-primary-500, #ED7500)` - and every consumer of
 * the token, including all the `colorScheme="primary"` call sites, resolves through that single
 * indirection with no component changes. `utils/applyThemeVars.ts` writes the overrides onto
 * `document.documentElement.style` once the tenant's theme arrives. With nothing set, the
 * fallbacks render, so first paint is identical to the static theme and nothing needs seeding.
 *
 * The authoritative set of overridable paths is `CustomerThemeSchema`
 * (`packages/utils/src/models/customer-theme/schema.ts`) - that schema is what the admin UI edits
 * against, so the paths passed to `themeVar()` here must match it exactly.
 *
 * Things that do NOT work as CSS variables:
 *
 * 1. `colorScheme` is a *palette name*, not a value, so `colorScheme={'var(...)'}` is meaningless.
 *    A tenant recolours the `primary` scale; it cannot redirect a button to a different scale.
 *    This is exactly why the variables live in the token values rather than at call sites.
 *
 * 2. `breakpoints` (below) are consumed inside `@media (min-width: ...)`, where `var()` is invalid
 *    CSS. Never var-ify them.
 *
 * 3. Chakra colour math (`transparentize`/`darken`/`whiten`/`tone` from `@chakra-ui/theme-tools`)
 *    bakes from the raw hex at theme-build time and will not follow a runtime override. Worse, it
 *    fails silently: `getColor` wraps `toHex` in try/catch and returns `#000000`, so a var-ified
 *    scale degrades to black rather than crashing. In the base theme this is reachable from
 *    Button `ghost`/`outline`, `Badge` and `Alert` (all `_dark`-only, and dead today because no
 *    colour mode is configured), and - in *light* mode - from the `Tabs` `soft-rounded` variant
 *    and an indeterminate `Progress`. Neither of those two is used by this app, but enabling dark
 *    mode or adopting `soft-rounded` tabs would surface the trap.
 *
 * 4. `logo.*` is layout applied as React props, not paint, so it is deliberately excluded from the
 *    variable path (as are `copy`, `assets` and `fonts.url`); `flattenThemeToCssVars` skips them.
 */

const label = {
  fontSize: themeVar('label.fontSize', 'var(--chakra-fontSizes-xs)'),
  fontWeight: themeVar('label.fontWeight', '600'),
  lineHeight: '12px',
  letterSpacing: themeVar('label.letterSpacing', '0.05em'),
  textTransform: themeVar('label.textTransform', 'uppercase'),
};

// The tenant spread stays last, so a statically configured tenant `secondary` keeps beating the
// blue default exactly as before.
const staticColors: Record<string, Record<string, string>> = {
  ...foundations.colors,
  secondary: foundations.colors.blue,
  ...(getTenantByHost()?.customerTheme?.colors ?? {}),
};

/**
 * Re-points a colour scale at `--peli-colors-<name>-<shade>`, keeping the resolved static value as
 * the `var()` fallback so the tenant's own hex still paints on first render. Only shades that
 * already exist are wrapped: minting a token that was previously absent would leave it with
 * nothing to fall back to.
 */
const themeableScale = (name: 'primary' | 'secondary') =>
  Object.fromEntries(
    Object.entries(staticColors[name] ?? {}).map(([shade, value]) => [
      shade,
      themeVar(`colors.${name}.${shade}`, value),
    ]),
  );

export const theme = extendTheme({
  ...foundations,
  colors: {
    ...staticColors,
    primary: themeableScale('primary'),
    secondary: themeableScale('secondary'),
  },
  fontSizes: {
    xs: '0.75rem',
    sm: '0.8125rem',
    md: '0.875rem',
    lg: '1rem',
    xl: '1.125rem',
    '2xl': '1.25rem',
    '3xl': '1.5rem',
    '4xl': '1.875rem',
    '5xl': '2.25rem',
    '6xl': '3rem',
    '7xl': '3.75rem',
    '8xl': '4.5rem',
    '9xl': '6rem',
  },
  textStyles: {
    // Body
    bodyRegular: {
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '28px',
      color: 'text.default',
    },
    bodyMedium: {
      fontWeight: 500,
      fontSize: '16px',
      lineHeight: '28px',
      color: 'text.default',
    },
    bodySemibold: {
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '28px',
      color: 'text.default',
    },
    // Body Small
    bodySmallRegular: {
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '24px',
      color: 'text.paragraph',
    },
    bodySmallMedium: {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '24px',
      color: 'text.default',
    },
    bodySmallSemibold: {
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '24px',
      color: 'text.default',
    },
    // Caption
    captionRegular: {
      fontWeight: 400,
      fontSize: '13px',
      lineHeight: '20px',
      color: 'text.default',
    },
    captionMedium: {
      fontWeight: 500,
      fontSize: '13px',
      lineHeight: '20px',
      color: 'text.default',
    },
    captionSemibold: {
      fontWeight: 600,
      fontSize: '13px',
      lineHeight: '20px',
      color: 'text.default',
    },
    // Caption Small
    captionSmallRegular: {
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      color: 'text.subtle',
    },
    captionSmallMedium: {
      fontWeight: 500,
      fontSize: '12px',
      lineHeight: '16px',
      color: 'text.default',
    },
    captionSmallSemibold: {
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '16px',
      color: 'text.default',
    },
    label,
  },
  breakpoints: {
    base: '100%',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1440px',
  },
  components: {
    ...baseTheme.components,
    Card: card,
    Tag: tag,
    Table,
    LabeledValue: labeledValue,
    NewsBanner: newsBanner,
    Button,
    CustomRadio,
    SimpleField,
    SimpleTooltip,
    FormLabel: defineStyleConfig({
      variants: {
        inputLabel: label,
      },
    }),
    FormError: formError,
    RadioCard,
    Heading,
    Modal: defineStyleConfig({
      baseStyle: {
        dialog: {
          maxWidth: '7xl',
        },
      },
    }),
  },
});

export const statusColorScheme = {
  [RequestStatus.New]: 'orange',
  [RequestStatus.QuotingInProgress]: 'orange',
  [RequestStatus.QuotingPending]: 'orange',
  [RequestStatus.QuotingComplete]: 'green',
  [RequestStatus.InfoPending]: 'orange',
  [RequestStatus.BookingRequested]: 'orange',
  [RequestStatus.BookingChange]: 'orange',
  [RequestStatus.BookingPending]: 'orange',
  [RequestStatus.BookingComplete]: 'blue',
  [RequestStatus.BookingCancel]: 'orange',
  [RequestStatus.Cancelled]: 'red',
} satisfies Record<RequestStatus, string>;

export const shipmentStatusColorScheme = {
  [ShipmentStatus.PENDING]: 'gray',
  [ShipmentStatus.PICKUP_SCHEDULED]: 'orange',
  [ShipmentStatus.PICKUP_COMPLETE]: 'green',
  [ShipmentStatus.AT_WAREHOUSE]: 'orange',
  [ShipmentStatus.AIRLINE_ACCEPTED]: 'green',
  [ShipmentStatus.OUT_FOR_DELIVERY]: 'green',
  [ShipmentStatus.DEPARTED]: 'green',
  [ShipmentStatus.ARRIVED]: 'green',
  [ShipmentStatus.DELIVERED]: 'green',
  [ShipmentStatus.ON_HOLD]: 'red',
} satisfies Record<ShipmentStatus, string>;

// Allows auto-completion of custom tokens
declare module '@chakra-ui/styled-system' {
  export interface CustomThemeTypings extends BaseThemeTypings {
    colors: keyof typeof foundations.semanticTokens.colors;
  }
}
